import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { connect } from 'react-redux';
import { getDeviceErrors, getDeviceErrorsCount, exportDeviceErrors } from '../../stores/actions/actions';
import { getSystemsNodesShort } from '../../stores/actions/logsActions';
import { downloadBlob, withTimestamp } from '../../helpers/exportCsv';

interface DeviceErrorsProps {
    mobileView: boolean;
    dbDeviceErrors: any[];
    dbDeviceErrorsCount: number;
    deviceErrorsLoading: boolean;
    systemsNodesShort: any[];
    getDeviceErrors: (params: any) => void;
    getDeviceErrorsCount: (params: any) => void;
    exportDeviceErrors: (filters: any) => Promise<any[]>;
    getSystemsNodesShort: () => void;
}

interface DeviceErrorsParams {
    limit: number;
    offset: number;
    sortField: string;
    sortOrder: 1 | -1;
    systemId: number | null;
    nodeId: number | null;
    deviceId: string;
    serialNumber: string;
    errorMessage: string;
    dateStart: string | null;
    dateEnd: string | null;
}

const defaultParams: DeviceErrorsParams = {
    limit: 25,
    offset: 0,
    sortField: 'timestamp',
    sortOrder: -1,
    systemId: null,
    nodeId: null,
    deviceId: '',
    serialNumber: '',
    errorMessage: '',
    dateStart: null,
    dateEnd: null,
};

const DeviceErrors = ({
    mobileView,
    dbDeviceErrors,
    dbDeviceErrorsCount,
    deviceErrorsLoading,
    systemsNodesShort,
    getDeviceErrors,
    getDeviceErrorsCount,
    exportDeviceErrors,
    getSystemsNodesShort
}: DeviceErrorsProps) => {

    const dt = useRef(null);
    const [exporting, setExporting] = useState(false);
    const exportAbortController = useRef<AbortController | null>(null);

    useEffect(() => () => exportAbortController.current?.abort(), []);

    const [deviceErrors, setDeviceErrors] = useState([]);
    const [params, setParams] = useState<DeviceErrorsParams>(defaultParams);

    const fetchCount = (p: DeviceErrorsParams) => {
        getDeviceErrorsCount({
            systemId: p.systemId,
            nodeId: p.nodeId,
            deviceId: p.deviceId,
            serialNumber: p.serialNumber,
            errorMessage: p.errorMessage,
            dateStart: p.dateStart,
            dateEnd: p.dateEnd,
        });
    };

    useEffect(() => {
        getSystemsNodesShort();
        getDeviceErrors(params);
        fetchCount(params);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dbDeviceErrors) {
            setDeviceErrors(dbDeviceErrors.map((row, index) => ({
                ...row,
                rowId: (row.id != null && row.systemId != null) ? `${row.id}-${row.systemId}` : `idx-${index}`,
            })));
        }
    }, [dbDeviceErrors]);

    const systemOptions = useMemo(() => {
        if (!systemsNodesShort) return [];
        const byId = new Map();
        systemsNodesShort.forEach(n => {
            if (n.systemId != null && !byId.has(n.systemId)) {
                byId.set(n.systemId, n.systemName);
            }
        });
        return Array.from(byId.entries())
            .map(([systemId, systemName]) => ({ label: systemName, value: systemId }))
            .sort((a, b) => a.label?.localeCompare(b.label));
    }, [systemsNodesShort]);

    const nodeOptionsFor = (systemId: number | null) => {
        if (!systemsNodesShort || systemId == null) return [];
        return systemsNodesShort
            .filter(n => n.systemId === systemId)
            .map(n => ({ label: n.nodeName, value: n.nodeId }))
            .sort((a, b) => a.label?.localeCompare(b.label));
    };

    const onPage = (event) => {
        const newParams = { ...params, limit: event.rows, offset: event.first };
        setParams(newParams);
        getDeviceErrors(newParams);
    };

    const onSort = (event) => {
        const newParams = {
            ...params,
            sortField: event.sortField || 'timestamp',
            sortOrder: (event.sortOrder || -1) as 1 | -1,
        };
        setParams(newParams);
        getDeviceErrors(newParams);
    };

    // Filters just update local state - nothing is fetched until "Apply Filters"
    const updateFilter = (field: string, value: any) => {
        setParams(prev => ({ ...prev, [field]: value, offset: 0 }));
    };

    const updateSystemFilter = (systemId: number | null) => {
        setParams(prev => ({ ...prev, systemId, nodeId: null, deviceId: '', offset: 0 }));
    };

    const handleDateChange = (field: 'dateStart' | 'dateEnd', date: Date | null) => {
        if (!date) {
            updateFilter(field, null);
            return;
        }
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        updateFilter(field, utcDate.toISOString());
    };

    const applyFilters = () => {
        getDeviceErrors(params);
        fetchCount(params);
    };

    const clearAllFilters = () => {
        setParams(defaultParams);
        getDeviceErrors(defaultParams);
        fetchCount(defaultParams);
    };

    const exportDeviceErrorsHandler = () => {
        const controller = new AbortController();
        exportAbortController.current = controller;
        setExporting(true);
        exportDeviceErrors(
            {
                systemId: params.systemId,
                nodeId: params.nodeId,
                deviceId: params.deviceId,
                serialNumber: params.serialNumber,
                errorMessage: params.errorMessage,
                dateStart: params.dateStart,
                dateEnd: params.dateEnd,
            },
            controller.signal
        )
            .then((blob: Blob) => downloadBlob(`${withTimestamp('device-errors')}.csv`, blob))
            .catch((err) => { if (err?.name !== 'AbortError') console.error(err); })
            .finally(() => {
                setExporting(false);
                exportAbortController.current = null;
            });
    };

    const cancelExport = () => {
        exportAbortController.current?.abort();
    };

    const handleQuickSystemChange = (systemId: number | null) => {
        const newParams = { ...params, systemId, nodeId: null, deviceId: '', offset: 0 };
        setParams(newParams);
        getDeviceErrors(newParams);
        fetchCount(newParams);
    };

    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [pendingFilters, setPendingFilters] = useState({
        nodeId: params.nodeId,
        deviceId: params.deviceId,
        serialNumber: params.serialNumber,
        errorMessage: params.errorMessage,
        dateStart: params.dateStart,
        dateEnd: params.dateEnd,
    });
    const [pendingSortField, setPendingSortField] = useState(params.sortField);
    const [pendingSortOrder, setPendingSortOrder] = useState<-1 | 1>(params.sortOrder);

    const openMobileFilterDialog = () => {
        setPendingFilters({
            nodeId: params.nodeId,
            deviceId: params.deviceId,
            serialNumber: params.serialNumber,
            errorMessage: params.errorMessage,
            dateStart: params.dateStart,
            dateEnd: params.dateEnd,
        });
        setPendingSortField(params.sortField);
        setPendingSortOrder(params.sortOrder);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        const newParams = {
            ...params,
            sortField: pendingSortField,
            sortOrder: pendingSortOrder,
            ...pendingFilters,
            offset: 0,
        };
        setParams(newParams);
        getDeviceErrors(newParams);
        fetchCount(newParams);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        setPendingFilters({ nodeId: null, deviceId: '', serialNumber: '', errorMessage: '', dateStart: null, dateEnd: null });
        setPendingSortField('timestamp');
        setPendingSortOrder(-1);
    };

    const activeFilterCount = useMemo(() => {
        return [params.nodeId, params.deviceId, params.serialNumber, params.errorMessage, params.dateStart, params.dateEnd]
            .filter(v => v !== '' && v !== null).length;
    }, [params]);

    const sortOptions = [
        { label: 'System', value: 'systemName' },
        { label: 'Node name', value: 'nodeName' },
        { label: 'WSN Device ID', value: 'deviceId' },
        { label: 'Serial number', value: 'serialNumber' },
        { label: 'Error message', value: 'errorMessage' },
        { label: 'Date (UTC)', value: 'timestamp' },
    ];

    const timestampTemplate = (rowData: any) => {
        if (!rowData.timestamp) {
            return null;
        }
        const timestampDate = new Date(rowData.timestamp);
        const dateStr = timestampDate.toLocaleString('sv-SE', { timeZone: 'UTC' }).replace('T', ' ');

        return <div>{dateStr}</div>;
    };

    const systemFilterElement = () => (
        <Dropdown
            value={params.systemId}
            options={systemOptions}
            onChange={(e) => updateSystemFilter(e.value)}
            placeholder="Search system..."
            showClear
            filter
            className="p-column-filter"
            style={{ minWidth: '12rem' }}
        />
    );

    const nodeFilterElement = () => (
        <Dropdown
            value={params.nodeId}
            options={nodeOptionsFor(params.systemId)}
            onChange={(e) => updateFilter('nodeId', e.value)}
            placeholder={params.systemId ? 'Search node...' : 'Select system first'}
            disabled={!params.systemId}
            showClear
            filter
            className="p-column-filter"
            style={{ minWidth: '12rem' }}
        />
    );

    const deviceIdFilterElement = () => (
        <InputText
            value={params.deviceId}
            onChange={(e) => updateFilter('deviceId', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder={params.systemId ? '' : 'Select system first'}
            disabled={!params.systemId}
            className="w-full"
        />
    );

    const serialNumberFilterElement = () => (
        <InputText
            value={params.serialNumber}
            onChange={(e) => updateFilter('serialNumber', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            className="w-full"
        />
    );

    const errorMessageFilterElement = () => (
        <InputText
            value={params.errorMessage}
            onChange={(e) => updateFilter('errorMessage', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            className="w-full"
        />
    );

    const timestampFilterElement = () => (
        <div className="flex flex-column gap-1">
            <Calendar
                value={params.dateStart ? new Date(params.dateStart) : null}
                onChange={(e) => handleDateChange('dateStart', e.value as Date | null)}
                placeholder="From"
                dateFormat="yy-mm-dd"
                mask="9999-99-99"
                showButtonBar
                style={{ minWidth: '10rem' }}
            />
            <Calendar
                value={params.dateEnd ? new Date(params.dateEnd) : null}
                onChange={(e) => handleDateChange('dateEnd', e.value as Date | null)}
                placeholder="To"
                dateFormat="yy-mm-dd"
                mask="9999-99-99"
                showButtonBar
                style={{ minWidth: '10rem' }}
            />
        </div>
    );

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <Dropdown
                value={params.systemId}
                options={systemOptions}
                onChange={(e) => handleQuickSystemChange(e.value)}
                placeholder="Search system..."
                showClear
                filter
                className="flex-1"
            />
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span
                        className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
                    >
                        {activeFilterCount}
                    </span>
                )}
            </div>
        </div>
    );

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.systemName || '---'}</span>
                        <span className="text-sm text-color-secondary">{timestampTemplate(rowData)}</span>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-6">
                                <div className="text-xs text-color-secondary">Node name</div>
                                <div className="font-medium">{rowData.nodeName || '---'}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-xs text-color-secondary">WSN Device ID</div>
                                <div className="font-medium">{rowData.deviceId ?? '---'}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-xs text-color-secondary">Serial number</div>
                                <div className="font-medium">{rowData.serialNumber || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="p-2 surface-50 border-round">
                        <div className="text-xs text-color-secondary">Error message</div>
                        <div className="font-medium">{rowData.errorMessage || '---'}</div>
                    </div>

                </div>
            </div>
        );
    };

    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );

    const loadingIndicator = (
        <div className="flex items-center">
            <i className="pi pi-spin pi-spinner text-purple-500 text-2xl" />
            <span className="ml-2 text-gray-600">Loading data…</span>
        </div>
    );
    const mobileLoadingIndicator = (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-spin pi-spinner text-purple-500" />
            <span className="text-color-secondary text-sm">Loading data…</span>
        </div>
    );

    const loadingSpacer = <div style={{ visibility: 'hidden' }}>{loadingIndicator}</div>;
    const mobileLoadingSpacer = <div style={{ visibility: 'hidden' }}>{mobileLoadingIndicator}</div>;

    const centerToolbarTemplate = () => {
        return (
            <div className="flex items-center" style={{ width: "200px" }}>
                {deviceErrorsLoading ? loadingIndicator : null}
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Apply Filters" icon="pi pi-check" onClick={applyFilters} />
                <Button label="Clear" icon="pi pi-times" severity="secondary" onClick={clearAllFilters} />
                <Button label="Export" icon="pi pi-file-export" outlined loading={exporting} onClick={exportDeviceErrorsHandler} />
                {exporting && (
                    <div className="flex align-items-center gap-2">
                        <small className="text-color-secondary">
                            Exporting… this can take a moment for a large result set
                        </small>
                        <Button label="Cancel" icon="pi pi-times" text severity="secondary" onClick={cancelExport} />
                    </div>
                )}
            </div>
        );
    };

    const dialogNodeOptions = useMemo(
        () => nodeOptionsFor(params.systemId),
        [systemsNodesShort, params.systemId]
    );

    const dataView = useMemo(() => {
        return (
            <div>
                {mobileFilterBar}
                {deviceErrorsLoading && (
                    <div className="mb-3">
                        {mobileLoadingIndicator}
                    </div>
                )}
                <DataView
                    value={deviceErrors}
                    itemTemplate={cardTemplate}
                    lazy
                    paginator
                    first={params.offset}
                    rows={params.limit}
                    totalRecords={dbDeviceErrorsCount || 0}
                    onPage={onPage}
                    paginatorLeft={deviceErrorsLoading ? mobileLoadingSpacer : null}
                    paginatorRight={deviceErrorsLoading ? mobileLoadingIndicator : null}
                />
            </div>
        );
    }, [deviceErrors, deviceErrorsLoading, params, dbDeviceErrorsCount, systemOptions, activeFilterCount]);

    const dataTable = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-4" center={centerToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt} value={deviceErrors}
                        lazy
                        dataKey="rowId" paginator
                        first={params.offset} rows={params.limit} rowsPerPageOptions={[10, 25, 50, 100]}
                        totalRecords={dbDeviceErrorsCount || 0}
                        onPage={onPage} onSort={onSort}
                        sortField={params.sortField} sortOrder={params.sortOrder}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                        paginatorLeft={deviceErrorsLoading ? loadingSpacer : null}
                        paginatorRight={deviceErrorsLoading ? loadingIndicator : null}
                        filterDisplay="row" stripedRows size='small' resizableColumns >
                <Column
                    field="systemName"
                    header="System"
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={systemFilterElement}>
                </Column>
                <Column
                    field="nodeName"
                    header="Node name"
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={nodeFilterElement}>
                </Column>
                <Column
                    field="deviceId"
                    header="WSN Device ID"
                    style={{ maxWidth: '140px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={deviceIdFilterElement}>
                </Column>
                <Column
                    field="serialNumber"
                    header="Serial number"
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={serialNumberFilterElement}>
                </Column>
                <Column
                    field="errorMessage"
                    header="Error message"
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={errorMessageFilterElement}>
                </Column>
                <Column
                    field="timestamp"
                    header="Date (UTC)"
                    body={timestampTemplate}
                    style={{ maxWidth: '200px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={timestampFilterElement}>
                </Column>
                </DataTable>
            </div>
        );
    }, [deviceErrors, deviceErrorsLoading, params, dbDeviceErrorsCount, systemOptions, exporting]);

    return (
        <div>
            { mobileView ? dataView : dataTable }

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter & Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingSortField}
                            options={sortOptions}
                            onChange={(e) => setPendingSortField(e.value)}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingSortOrder(prev => prev === 1 ? -1 : 1)}
                            tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Node name</label>
                    <Dropdown
                        value={pendingFilters.nodeId}
                        options={dialogNodeOptions}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, nodeId: e.value }))}
                        placeholder={params.systemId ? 'Any' : 'Select a system first'}
                        disabled={!params.systemId}
                        showClear
                        filter
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">WSN Device ID</label>
                    <InputText value={pendingFilters.deviceId}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, deviceId: e.target.value }))}
                        placeholder={params.systemId ? 'Search...' : 'Select a system first'}
                        disabled={!params.systemId} />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Serial number</label>
                    <InputText value={pendingFilters.serialNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, serialNumber: e.target.value }))}
                        placeholder="Search..." />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Error message</label>
                    <InputText value={pendingFilters.errorMessage}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, errorMessage: e.target.value }))}
                        placeholder="Search..." />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date from</label>
                    <Calendar
                        value={pendingFilters.dateStart ? new Date(pendingFilters.dateStart) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) {
                                setPendingFilters(prev => ({ ...prev, dateStart: null }));
                                return;
                            }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingFilters(prev => ({ ...prev, dateStart: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd"
                        dateFormat="yy-mm-dd"
                        mask="9999-99-99"
                        showButtonBar
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date to</label>
                    <Calendar
                        value={pendingFilters.dateEnd ? new Date(pendingFilters.dateEnd) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) {
                                setPendingFilters(prev => ({ ...prev, dateEnd: null }));
                                return;
                            }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingFilters(prev => ({ ...prev, dateEnd: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd"
                        dateFormat="yy-mm-dd"
                        mask="9999-99-99"
                        showButtonBar
                        className="w-full"
                    />
                </div>
            </Dialog>
        </div>
    );
}


const mapStateToProps = (state) => {
    return {
        mobileView: state.dashboard.mobileView,
        dbDeviceErrors: state.installer.deviceErrors,
        dbDeviceErrorsCount: state.installer.deviceErrorsCount,
        deviceErrorsLoading: state.installer.deviceErrorsLoading,
        systemsNodesShort: state.dashboard.systemsNodesShort,
    };
};

const actions = {
    getDeviceErrors,
    getDeviceErrorsCount,
    exportDeviceErrors,
    getSystemsNodesShort,
};

export default connect(mapStateToProps, actions)(DeviceErrors);
